<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  session: {
    type: Object,
    required: true,
  },
  status: {
    type: String,
    required: true,
  },
  qrCode: {
    type: String,
    default: '',
  },
  isRefreshing: {
    type: Boolean,
    default: false,
  },
  pendingAction: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['refresh', 'action']);

const STATUS_COLORS = {
  connected: 'bg-n-teal-9',
  scan_qr: 'bg-n-amber-9',
  starting: 'bg-n-amber-9',
  connecting: 'bg-n-amber-9',
  failed: 'bg-n-ruby-9',
};

const statusColor = computed(
  () => STATUS_COLORS[props.status] || 'bg-n-slate-8'
);
const isWaitingForQr = computed(() => props.status === 'scan_qr');
const isRunning = computed(() =>
  ['connected', 'scan_qr', 'starting', 'connecting'].includes(props.status)
);

// Logging out unlinks the phone: the first click arms it for a few seconds
const confirmingLogout = ref(false);
let confirmTimer = null;

const logout = () => {
  if (!confirmingLogout.value) {
    confirmingLogout.value = true;
    confirmTimer = setTimeout(() => {
      confirmingLogout.value = false;
    }, 4000);
    return;
  }
  clearTimeout(confirmTimer);
  confirmingLogout.value = false;
  emit('action', 'logout');
};

onBeforeUnmount(() => clearTimeout(confirmTimer));
</script>

<template>
  <div
    class="flex flex-col rounded-xl outline outline-1 -outline-offset-1 outline-n-weak"
  >
    <div class="flex flex-wrap items-center justify-between gap-4 px-4 py-4">
      <div class="flex items-center gap-3 min-w-0">
        <span class="relative flex size-3 shrink-0">
          <span
            v-if="isWaitingForQr || status === 'starting'"
            class="absolute inline-flex size-full rounded-full opacity-60 animate-ping"
            :class="statusColor"
          />
          <span
            class="relative inline-flex size-3 rounded-full"
            :class="statusColor"
          />
        </span>
        <div class="flex flex-col min-w-0">
          <span class="text-heading-3 text-n-slate-12">
            {{ $t(`INBOX_MGMT.ADD.WAHA.STATUS.${status.toUpperCase()}`) }}
          </span>
          <span class="text-sm text-n-slate-11 truncate">
            {{ session.phone_number || $t('INBOX_MGMT.WAHA.CONNECTION.NO_PHONE') }}
            <template v-if="session.engine">
              · {{ session.engine }}
            </template>
            · {{ session.session_name }}
          </span>
        </div>
      </div>
      <NextButton
        sm
        faded
        slate
        icon="i-lucide-refresh-cw"
        :is-loading="isRefreshing"
        :label="$t('INBOX_MGMT.WAHA.REFRESH_STATUS')"
        @click="emit('refresh')"
      />
    </div>

    <div
      v-if="isWaitingForQr"
      class="flex flex-col sm:flex-row items-center gap-5 px-4 py-4 border-t border-n-weak"
    >
      <img
        v-if="qrCode"
        :src="qrCode"
        :alt="$t('INBOX_MGMT.WAHA.SCAN_QR')"
        class="size-56 shrink-0 rounded-xl bg-white p-3 outline outline-1 outline-n-weak"
      />
      <div class="flex flex-col gap-2">
        <span class="text-heading-3 text-n-slate-12">
          {{ $t('INBOX_MGMT.WAHA.SCAN_QR') }}
        </span>
        <span class="text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.WAHA.CONNECTION.QR_HELP') }}
        </span>
      </div>
    </div>

    <div
      class="flex flex-wrap items-center gap-2 px-4 py-3 border-t border-n-weak"
    >
      <NextButton
        v-if="!isRunning"
        sm
        icon="i-lucide-play"
        :is-loading="pendingAction === 'start'"
        :label="$t('INBOX_MGMT.WAHA.START')"
        @click="emit('action', 'start')"
      />
      <NextButton
        sm
        faded
        slate
        icon="i-lucide-rotate-ccw"
        :is-loading="pendingAction === 'restart'"
        :label="$t('INBOX_MGMT.WAHA.RESTART')"
        @click="emit('action', 'restart')"
      />
      <NextButton
        v-if="isRunning"
        sm
        faded
        slate
        icon="i-lucide-square"
        :is-loading="pendingAction === 'stop'"
        :label="$t('INBOX_MGMT.WAHA.STOP')"
        @click="emit('action', 'stop')"
      />
      <NextButton
        sm
        faded
        slate
        icon="i-lucide-plug-zap"
        :is-loading="pendingAction === 'recreate_app'"
        :label="$t('INBOX_MGMT.WAHA.RECREATE_APP')"
        @click="emit('action', 'recreate_app')"
      />
      <NextButton
        sm
        :faded="!confirmingLogout"
        ruby
        icon="i-lucide-log-out"
        class="ltr:ml-auto rtl:mr-auto"
        :is-loading="pendingAction === 'logout'"
        :label="
          confirmingLogout
            ? $t('INBOX_MGMT.WAHA.CONNECTION.LOGOUT_CONFIRM')
            : $t('INBOX_MGMT.WAHA.LOGOUT')
        "
        @click="logout"
      />
    </div>
  </div>
</template>
